import "./PageButtons.scss";

import clsx from "clsx";
import { FC } from "react";

import { ConfirmGroupButton } from "components/footer/controls/ConfirmGroupButton";
import { StudentSubmitButton } from "components/footer/controls/StudentSubmitButton";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ChevronUpIcon } from "mds/icons";
import { useWorksheetLoaded } from "sharedb/ShareDBDoc";
import { useAppSelector } from "store/index";
import { selectCurrentUserId, selectPageById, selectPageGroupUserForUser } from "store/selectors";
import { useShouldShowStudentSubmitButton } from "utils/page";

type PageButtonsProps = {
  containerHasVerticalScrollbar: boolean;
  pageId: string;
  showBackToTop: boolean;
  scrollBackToTop: () => void;
};

export const PageButtons: FC<PageButtonsProps> = ({
  containerHasVerticalScrollbar,
  pageId,
  showBackToTop,
  scrollBackToTop,
}) => {
  const userId = useAppSelector(selectCurrentUserId);
  const selectedPage = useAppSelector((s) => selectPageById(s, pageId));
  const isDocLoaded = useWorksheetLoaded(selectedPage?.worksheet_id);
  const shouldShowStudentSubmitButton = useShouldShowStudentSubmitButton(selectedPage, isDocLoaded);
  const pageGroupUser = useAppSelector((s) =>
    selectPageGroupUserForUser(s, selectedPage?.id, userId),
  );
  const shouldShowConfirmGroupButton = pageGroupUser && !pageGroupUser.is_ready;

  return (
    <>
      {(shouldShowStudentSubmitButton || shouldShowConfirmGroupButton) && (
        <div
          className={clsx(
            "page-buttons-divider extended-page-block-width mt-[-10px]",
            // The height of the blue divider determines how far the submit button is from the divider
            // and is different depending on whether the container has a vertical scrollbar or not.
            containerHasVerticalScrollbar ? "min-h-[46px]" : "min-h-[26px]",
          )}
        >
          <div className="h-[2px] w-full max-w-[var(--page-default-max-width)] bg-blue-tint-90" />
        </div>
      )}
      <div
        className={clsx(
          "page-buttons extended-page-block-width pointer-events-none sticky bottom-5 flex justify-end gap-2",
          showBackToTop ? "show-back-to-top" : "hide-back-to-top",
        )}
      >
        {shouldShowStudentSubmitButton ? (
          <div className="page-buttons-cta pointer-events-auto">
            <StudentSubmitButton selectedPage={selectedPage} />
          </div>
        ) : shouldShowConfirmGroupButton ? (
          <div className="page-buttons-cta pointer-events-auto">
            <ConfirmGroupButton selectedPage={selectedPage} userId={userId} />
          </div>
        ) : null}
        <Button
          aria-label={t("common.back_to_top")}
          className={clsx(
            "page-buttons-top h-[44px] w-[44px] rounded-full",
            shouldShowStudentSubmitButton || shouldShowConfirmGroupButton ? "absolute" : "",
            showBackToTop ? "pointer-events-auto" : "",
          )}
          eventName="Back to top button"
          kind="white"
          onClick={scrollBackToTop}
        >
          <ChevronUpIcon />
        </Button>
      </div>
    </>
  );
};
